import React from 'react';
import { useTranslation } from 'react-i18next';
import './StatusProgressBar.css';

type RequestStatus = 'DRAFT' | 'UNDER_REVIEW' | 'PUBLISHED' | 'UNDER_SELECTION' | 'ACCEPTED' | 'PLANNED' | 'IN_TRANSIT' | 'COMPLETED' | 'CLOSED' | 'WITHDRAWN';

interface StatusStep {
  key: RequestStatus;
  labelKey: string;
  shortLabelKey: string;
}

const REQUEST_STEPS: StatusStep[] = [
  { key: 'DRAFT', labelKey: 'components.progressBar.draft', shortLabelKey: 'components.progressBar.draft' },
  { key: 'UNDER_REVIEW', labelKey: 'status.underReview', shortLabelKey: 'components.progressBar.review' },
  { key: 'PUBLISHED', labelKey: 'status.published', shortLabelKey: 'components.progressBar.published' },
  { key: 'UNDER_SELECTION', labelKey: 'status.underSelection', shortLabelKey: 'components.progressBar.selection' },
  { key: 'IN_TRANSIT', labelKey: 'status.inTransit', shortLabelKey: 'components.progressBar.inTransit' },
  { key: 'COMPLETED', labelKey: 'status.completed', shortLabelKey: 'components.progressBar.completed' },
];

const TERMINAL_STATUSES: RequestStatus[] = ['CLOSED', 'WITHDRAWN'];

function getStepIndex(status: RequestStatus): number {
  // ACCEPTED and PLANNED are not displayed as steps but map to IN_TRANSIT position
  if (status === 'ACCEPTED' || status === 'PLANNED') {
    return REQUEST_STEPS.findIndex(s => s.key === 'IN_TRANSIT');
  }
  return REQUEST_STEPS.findIndex(s => s.key === status);
}

interface StatusProgressBarProps {
  status: RequestStatus;
  compact?: boolean;
}

function StatusProgressBar({ status, compact = false }: StatusProgressBarProps) {
  const { t } = useTranslation();
  const isTerminal = TERMINAL_STATUSES.includes(status);
  const currentIndex = getStepIndex(status);

  if (isTerminal) {
    return (
      <div className={`status-progress ${compact ? 'status-progress--compact' : ''}`}>
        <div className="status-progress__terminal">
          <span className={`status-progress__terminal-badge status-progress__terminal-badge--${status.toLowerCase()}`}>
            {status === 'CLOSED' ? t('status.closed') : t('status.withdrawn')}
          </span>
          <span className="status-progress__terminal-text">
            {status === 'CLOSED'
              ? t('status.closed')
              : t('status.withdrawn')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`status-progress ${compact ? 'status-progress--compact' : ''}`}>
      <div className="status-progress__track">
        {REQUEST_STEPS.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;

          return (
            <React.Fragment key={step.key}>
              {index > 0 && (
                <div
                  className={`status-progress__connector ${
                    isCompleted ? 'status-progress__connector--completed' :
                    isCurrent ? 'status-progress__connector--active' : ''
                  }`}
                />
              )}
              <div
                className={`status-progress__step ${
                  isCompleted ? 'status-progress__step--completed' :
                  isCurrent ? 'status-progress__step--current' :
                  'status-progress__step--future'
                }`}
                title={t(step.labelKey)}
              >
                <div className="status-progress__dot">
                  {isCompleted && (
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {isCurrent && <div className="status-progress__pulse" />}
                </div>
                {!compact && (
                  <span className="status-progress__label">
                    {compact ? t(step.shortLabelKey) : t(step.labelKey)}
                  </span>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default StatusProgressBar;
